import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SmartApiService } from './smart-api.service';

/** Forma mínima de la respuesta de `GET /smart/integration` que necesita la política. */
export interface IgtfPolicyIntegrationLike {
    success?: boolean;
    data?: { isSpecialTaxpayer?: boolean } | null;
}

/**
 * FINA-3330 — ÚNICA implementación en el front de la regla «IGTF solo para contribuyentes
 * especiales» (espejo de `utils/igtfPolicy.util.ts` del api):
 *
 *   sin integración fiscal (404 / null / success false) ⇒ true   (comportamiento actual: decide el switch)
 *   con integración                                      ⇒ data.isSpecialTaxpayer === true
 */
export const igtfAllowedFromIntegration = (integration: IgtfPolicyIntegrationLike | null | undefined): boolean => {
    if (!integration || integration.success === false || !integration.data) return true;
    return integration.data.isSpecialTaxpayer === true;
};

/**
 * Política del IGTF del comercio en sesión (patrón `BankAvailabilityService`: BehaviorSubject +
 * `refresh$()`; la API es la autoridad y responde 422 `IGTF_NOT_ALLOWED` si el front se equivoca).
 *
 * - `igtfAllowed === false` ⇒ el comercio es ordinario: se ocultan todos los controles de IGTF, el
 *   store nunca enciende `useIGTF` y los pre-checks «debes activar IGTF» no aplican.
 * - Desconocido ⇒ permitido: no se ocultan controles por una carga pendiente o un error de red.
 * - Se refresca al iniciar sesión, al abrir cada checkout (el resumen ya pide la integración) y en
 *   las pantallas de Preferencias y cobro de CxC: el cambio de condición aplica a la siguiente
 *   venta sin recargar la sesión del POS.
 */
@Injectable({
    providedIn: 'root',
})
export class IgtfPolicyService {
    readonly igtfAllowed$ = new BehaviorSubject<boolean>(true);

    constructor(private smartApiService: SmartApiService) {}

    get igtfAllowed(): boolean {
        return this.igtfAllowed$.value;
    }

    /** Alimentado por los componentes que YA piden la integración (sin HTTP extra). */
    applyIntegration(integration: IgtfPolicyIntegrationLike | null | undefined): boolean {
        const allowed = igtfAllowedFromIntegration(integration);
        if (allowed !== this.igtfAllowed) {
            this.igtfAllowed$.next(allowed);
        }
        return allowed;
    }

    /** `getSmartIntegration()` ya convierte errores en `null` ⇒ permitido. */
    refresh$(): Observable<boolean> {
        return this.smartApiService.getSmartIntegration().pipe(map(integration => this.applyIntegration(integration)));
    }

    load(): void {
        this.refresh$().subscribe();
    }

    /** Tras un 422 `IGTF_NOT_ALLOWED`: aplicar en el acto (apaga el IGTF del pedido) y releer en segundo plano. */
    markNotAllowed(): void {
        if (this.igtfAllowed) {
            this.igtfAllowed$.next(false);
        }
    }
}
